import { PartialTradeInfo } from '@optimex-xyz/trade-wallet-sdk'

import { IResponse, Model, ObjectsFactory } from '@/data'
import {
  ESTIMATE_TRADE_ENDPOINT,
  GET_TRADE_COMMITMENT_QUOTES,
  INIT_TRADE,
  SEND_PARTIAL_TRADE_INFO,
  TRADES_ENDPOINT,
} from '@/data/endpoint'
import { EstimateTradeRequest, InitiateTradeRequest, PrerefundRequest } from '@/types/sendTrade.type'
import { ITrade, ITradeRes } from '@/types/tradeDetail.type.ts'

export interface TradeDetailOptions {
  isRefetchInterval?: boolean
  skipInitialFetch?: boolean
}

export interface ListTradeParams {
  fromAddress: string[]
  page?: number
  limit?: number
  status?: any
  statuses?: string[]
}

export interface IInitTrade {
  tradeId: string
  depositAddress: string
  depositAmount: string
  payload: string
  approveAddress: string
  needApprove: boolean
  approvePayload: string
}

export interface IEstimateTrade {
  estimatedTime: number
  updatedAt: string
}

export interface ISubmitTx {
  tradeId: string
  txId: string
}

export interface IGetTradeHasCommitmentQuotesResponse {
  code: number
  message: string
  data: boolean // just true or false
}

export class Trade extends Model {
  static config = { endpoint: TRADES_ENDPOINT }

  static objects = ObjectsFactory.factory<ITrade>(Trade.config)

  static async getDetail(tradeId: string) {
    return await this.service.get<IResponse<ITrade>>({
      url: `${TRADES_ENDPOINT}/${tradeId}`,
    })
  }

  static async getListTrade(params: ListTradeParams) {
    return await this.service.get<ITradeRes>({
      url: TRADES_ENDPOINT,
      params,
    })
  }

  static async sendPartialTradeInfo(request: PartialTradeInfo) {
    const payload = {
      tradeId: request.tradeId,
      timestamp: Date.now(),
      data: request,
    }
    return await this.service.post({
      url: SEND_PARTIAL_TRADE_INFO,
      payload: payload,
    })
  }

  static async initTrade(request: InitiateTradeRequest) {
    return await this.service.post<IResponse<IInitTrade>>({
      url: INIT_TRADE,
      payload: request,
    })
  }

  static async estimate(params: EstimateTradeRequest) {
    return await this.service.get<IResponse<IEstimateTrade>>({
      url: ESTIMATE_TRADE_ENDPOINT,
      params,
    })
  }

  static async submitTx({ tradeId, txId }: ISubmitTx) {
    return await this.service.post({
      url: `${TRADES_ENDPOINT}/${tradeId}/submit-tx`,
      payload: { txId },
    })
  }

  static async prerefund({ tradeId, txId, timestamp }: PrerefundRequest) {
    return await this.service.post({
      url: `${TRADES_ENDPOINT}/${tradeId}/refund`,
      payload: { txId, timestamp },
    })
  }

  static async getTradeHasCommitmentQuotes(tradeId: string): Promise<IGetTradeHasCommitmentQuotesResponse> {
    return await this.service.get({
      url: `${GET_TRADE_COMMITMENT_QUOTES}/${tradeId}/has-commitment-quotes`,
    })
  }
}

Trade.init(Trade.config)
